import React, { useState } from 'react';
import type { SlimBannerProps } from '@stitch-fix/mode-react';
import type { HeaderProps } from '@stitch-fix/knit';
import { D } from '@mobily/ts-belt';
import { useRouter } from 'next/router';
import { useFeatureFlags } from '@stitch-fix/sf-next';
import { useAdultsIncentiveBanner } from './adults/useAdultsIncentiveBanner';
import { supFixIncentiveBannerSupportedPaths } from './adults/supFixIncentiveBannerSupportedPaths';
import { useKidsIncentiveBanner } from './kids/useKidsIncentiveBanner';
import { SignupIncentiveModal } from '../../components/Layout/SignupIncentiveBanner/SignupIncentiveModal';
import { SlimBannerModal, type SlimBannerModalProps } from '../../components/SlimBanner/SlimBannerModal';
import { shouldRenderSlimBannerModal } from '../../components/SlimBanner/SlimBanner';
import { useFrontDoorOffer } from '../useFrontDoorOffer';
import { useSupFixOffer } from '../useFrontDoorOffer/useSupFixOffer';
import { useVisitorDiscountOffer, UTM_BANNER_VARIANT_MAP } from './useVisitorDiscountOffer';
import { utmOfferIncentiveBannerSupportedPaths } from './adults/utmOfferIncentiveBannerSupportedPaths';

// eslint-disable-next-line no-shadow
export enum ModalCampaign {
  TWENTY_DOLLAR,
  THIRTY_DOLLAR,
  TWENTY_PERCENT,
  NO_PROMO,
  KIDS_FIRST_FIX,
}
export type UseIncentiveBannerProps = {
  hideHeaderFooter?: boolean;
  customSlimBanner?: HeaderProps['slimBanner'] & {
    modal: Omit<SlimBannerModalProps, 'isOpen' | 'close'>;
  };
};
const UTM_OFFER_FEATURE_FLAG = 'visitor.web.new_client_discount_offer_dynamic_copy' as const;
export const useIncentiveBanner = ({
  hideHeaderFooter = false,
  customSlimBanner = undefined
}: UseIncentiveBannerProps) => {
  const {
    asPath
  } = useRouter();
  const normalizedPath = asPath.split('?')[0];
  const isSUPFixRoute = supFixIncentiveBannerSupportedPaths.includes(normalizedPath);
  const isUTMOfferRoute = utmOfferIncentiveBannerSupportedPaths.includes(normalizedPath);
  const [showBannerPlaceholder, setShowBannerPlaceholder] = useState(!hideHeaderFooter);
  const [showModal, setShowModal] = useState(false);
  const modalClose = () => setShowModal(false);
  const handleBannerClick = () => setShowModal(true);
  const handleBannerLoad = () => setShowBannerPlaceholder(false);
  const {
    featureFlags,
    loading: flagLoading
  } = useFeatureFlags('visitor', UTM_OFFER_FEATURE_FLAG, {
    skip: !isUTMOfferRoute
  });
  const isDynamicCopyEnabled = featureFlags?.[UTM_OFFER_FEATURE_FLAG] === true;
  const {
    content: utmContent,
    offerName: utmOfferName,
    loading: utmLoading
  } = useVisitorDiscountOffer(hideHeaderFooter || !isDynamicCopyEnabled || !isUTMOfferRoute);
  const frontDoorOffer = useFrontDoorOffer(hideHeaderFooter || isSUPFixRoute);
  const supFixOffer = useSupFixOffer(hideHeaderFooter || !isSUPFixRoute);
  const {
    offer,
    slimBannerVariant: slimBannerVariantValue,
    loading: expLoading
  } = isSUPFixRoute ? supFixOffer : frontDoorOffer;
  const slimBannerVariant = slimBannerVariantValue as SlimBannerProps['variant'];
  const isLoading = flagLoading || utmLoading || expLoading;
  const {
    banner: adultBanner,
    slimBanner: adultSlimBanner
  } = useAdultsIncentiveBanner({
    onClick: handleBannerClick,
    onLoad: handleBannerLoad,
    variant: slimBannerVariant,
    isLoading,
    offer
  });
  const {
    banner: kidsBanner,
    slimBanner: kidsSlimBanner
  } = useKidsIncentiveBanner({
    onClick: handleBannerClick,
    onLoad: handleBannerLoad,
    variant: slimBannerVariant,
    isLoading
  });
  const utmSlimBanner: HeaderProps['slimBanner'] | undefined = !isLoading && utmContent ? {
    text: utmContent.bannerMessage,
    id: `utm-campaign-offer-${utmOfferName}`,
    cta: {
      text: utmContent.cta.label?.text,
      url: utmContent.cta.url ?? '/signup'
    },
    variant: UTM_BANNER_VARIANT_MAP[utmContent.bannerVariant] as SlimBannerProps['variant']
  } : undefined;
  let modalCampaign;

  // We ensure adult banners only show on adult pages and kids banners only show on kids pages
  // Therefore, we can use the banner existence to inform which modal campaign to use
  if (adultBanner || adultSlimBanner) {
    if (offer?.discount?.kind === 'fixed' && offer.discount.amountCents === 3000) {
      modalCampaign = ModalCampaign.THIRTY_DOLLAR;
    } else if (offer?.cell === 'no-promo') {
      modalCampaign = ModalCampaign.NO_PROMO;
    } else if (offer?.cell === 'percentage') {
      modalCampaign = ModalCampaign.TWENTY_PERCENT;
    } else {
      modalCampaign = ModalCampaign.TWENTY_DOLLAR;
    }
  } else if (kidsBanner || kidsSlimBanner) {
    modalCampaign = ModalCampaign.KIDS_FIRST_FIX;
  }
  const modal = <SignupIncentiveModal isOpen={showModal} close={modalClose} modalCampaign={modalCampaign} />;
  const {
    modal: customModalProps,
    ..._customSlimBannerProps
  } = customSlimBanner || {};
  const customSlimBannerProps = D.isEmpty(_customSlimBannerProps) ? undefined : _customSlimBannerProps as HeaderProps['slimBanner'];
  const customModal = customModalProps && shouldRenderSlimBannerModal(customModalProps) ? <SlimBannerModal isOpen={showModal} close={modalClose} heading={customModalProps.heading} body={customModalProps.body} image={customModalProps.image} cta={customModalProps.cta} /> : undefined;

  // We ensure adult banners only show on adult pages and kids banners only show on kids pages
  // Therefore, we can use this || logic in the banner and slimBanner return
  // values

  // On UTM routes, block fallback banners until all loading (flag, mutation, experiment) has resolved.
  const showFallback = !isUTMOfferRoute || !isLoading;
  const slimBanner = utmSlimBanner ?? (showFallback ? adultSlimBanner || kidsSlimBanner || customSlimBannerProps : undefined);
  return {
    showBannerPlaceholder,
    banner: adultBanner || kidsBanner,
    slimBanner,
    modal: modalCampaign !== undefined ? modal : customModal
  };
};